import { useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Loader2, User } from "lucide-react";
import type { WidgetUser } from "@/services/chat/chatService";

interface ChatSidebarProps {
  users: WidgetUser[];
  selectedUser: WidgetUser | null;
  onSelectUser: (user: WidgetUser) => void;
  onLoadMore: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  totalUnreadCount?: number;
}

export function ChatSidebar({
  users,
  selectedUser,
  onSelectUser,
  onLoadMore,
  hasMore,
  isLoading,
}: ChatSidebarProps) {
  const observerTarget = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && !isLoading) {
        onLoadMore();
      }
    },
    [hasMore, isLoading, onLoadMore]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
      rootMargin: "100px",
    });

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [handleObserver]);

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const dayMonth = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
    const time = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${dayMonth} • ${time}`;
  };

  return (
    <Card className={selectedUser ? "hidden lg:block" : ""}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Customers</span>
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0 pt-2">
        <div
          className="h-[calc(100vh-200px)] overflow-y-auto !min-w-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
          ref={scrollAreaRef}
        >
          <div className="space-y-1">
            {users?.map((customer, index) => {
              const isOnline = customer?.isOnline;
              const displayName = customer?.firstName
                ? `${customer?.firstName} ${customer?.lastName || ""}`.trim()
                : customer?.email || "Anonymous User";

              const avatarInitials =
                customer?.firstName && customer?.lastName
                  ? `${customer.firstName[0]}${customer.lastName[0]}`.toUpperCase()
                  : customer?.firstName
                  ? customer.firstName[0].toUpperCase()
                  : customer?.email
                  ? customer.email[0].toUpperCase()
                  : null;

              const lastMessageTime = formatDateTime(
                customer?.lastMessage?.createdAt
              );

              return (
                <div
                  key={`${customer?.id}-${index}`}
                  onClick={() => onSelectUser(customer)}
                  className={`p-4 cursor-pointer border-b w-full m-0 border-border/40 transition-all ${
                    selectedUser?.id === customer?.id
                      ? "bg-muted"
                      : "hover:bg-muted/60"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative flex-shrink-0">
                      <Avatar className="h-12 w-12 border border-border/40">
                        {avatarInitials ? (
                          <AvatarFallback className="text-sm font-semibold">
                            {avatarInitials}
                          </AvatarFallback>
                        ) : (
                          <AvatarFallback className="text-gray-400">
                            <User className="h-5 w-5" />
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div
                        className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background ${
                          isOnline ? "bg-green-500" : "bg-gray-400"
                        }`}
                      />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-base flex-1 truncate">
                          {displayName}
                        </p>
                        {lastMessageTime && (
                          <span className="text-[11px] text-muted-foreground/70 flex-shrink-0 whitespace-nowrap">
                            {lastMessageTime}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground ">
                        <span className="line-clamp-1">
                          {customer?.lastMessage?.text}
                        </span>
                        {customer?.lastMessage?.images?.[0] && (
                          <>
                            {customer?.lastMessage?.text && <span>•</span>}
                            <img
                              src={customer.lastMessage.images[0]}
                              alt="msg"
                              className="h-5 w-5 object-cover rounded-sm flex-shrink-0"
                            />
                            <span className="text-[11px] text-muted-foreground/70 whitespace-nowrap">
                              Photo
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={observerTarget} className="h-4 w-full" />
            {isLoading && (
              <div className="flex justify-center items-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}
            {!hasMore && users?.length > 0 && (
              <div className="text-center py-4 text-sm text-muted-foreground">
                All customers loaded
              </div>
            )}
            {!isLoading && users?.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No customers yet</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
